"use client";

import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import { LOCAL_STORAGE_SYNC_EVENT } from "@/src/hooks/useLocalStorageState";
import { SYNCED_KEYS, isHydratingPreferences, serverValueFor } from "@/src/lib/preferences";
import { updateUserSettings } from "@/src/helpers/dbFunctions";

const PUSH_DELAY_MS = 800;

// Pushes each local preference write to the account's settings map. Mount
// once near the root. The server's copy arriving at login is announced the
// same way, so that window is skipped. A failed push goes back in the queue
// (newer values win) and rides along with the next change.
export default function PreferencesSync() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const pending = useRef({});
  const timer = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const flush = () => {
      const patch = pending.current;
      pending.current = {};
      if (Object.keys(patch).length === 0) return;
      updateUserSettings({ getAccessTokenSilently, patch }).catch((error) => {
        console.error("Preferences did not sync:", error);
        pending.current = { ...patch, ...pending.current };
      });
    };
    const onChange = (event) => {
      const key = event.detail;
      if (!SYNCED_KEYS.has(key) || isHydratingPreferences()) return;
      const value = serverValueFor(key);
      pending.current[key] = value === undefined ? null : value; // cleared locally: back to the default everywhere
      clearTimeout(timer.current);
      timer.current = setTimeout(flush, PUSH_DELAY_MS);
    };
    window.addEventListener(LOCAL_STORAGE_SYNC_EVENT, onChange);
    return () => {
      // Another account may sign in next: nothing queued here may reach it.
      window.removeEventListener(LOCAL_STORAGE_SYNC_EVENT, onChange);
      clearTimeout(timer.current);
      pending.current = {};
    };
  }, [isAuthenticated, getAccessTokenSilently]);

  return null;
}
